import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
    UtilisateurChoice,
    UtilisateurSearchList,
    useModal,
} from '@europrocurement/flexy-components';
import { Contact } from '@europrocurement/l2d-domain';

export type ContactSelectionModalProps = {
    contacts: Contact[];
    currentContactId?: string;
    onContactSelect: (contact: Contact) => void;
    isLoading?: boolean;
};

/**
 * Modal de sélection d'un contact parmi une liste de contacts du prescripteur.
 * Permet de modifier l'assignation d'un dossier à un contact spécifique.
 */
const ContactSelectionModal: React.FunctionComponent<ContactSelectionModalProps> = function ({
    contacts,
    currentContactId,
    onContactSelect,
    isLoading = false,
}) {
    const { modalActions } = useModal();

    const [displayedContacts, setDisplayedContacts] = useState<UtilisateurChoice[]>([]);
    const [selectedContact, setSelectedContact] = useState<UtilisateurChoice | undefined>();

    const handleSearch = useCallback(
        (searchTerm?: string) => {
            const reg = new RegExp(searchTerm || '', 'isu');
            setDisplayedContacts(
                contacts
                    .map(
                        (contact) =>
                            ({
                                id: contact.id,
                                lastName: contact.nomContact || '',
                                firstName: contact.prenomContact || '',
                                email: contact.emailsContact[0]?.email || '',
                            }) as UtilisateurChoice,
                    )
                    .filter(
                        (u) =>
                            searchTerm === undefined ||
                            u.lastName.match(reg) ||
                            u.firstName.match(reg) ||
                            u.email.match(reg),
                    ),
            );
        },
        [setDisplayedContacts, contacts],
    );

    useEffect(() => {
        if (currentContactId) {
            const current = contacts.find((c) => c.id?.toString() === currentContactId.toString());
            if (current) {
                setSelectedContact({
                    id: current.id,
                    lastName: current.nomContact || '',
                    firstName: current.prenomContact || '',
                    email: current.emailsContact[0]?.email || '',
                } as UtilisateurChoice);
            }
        }
    }, [currentContactId, contacts]);

    useEffect(() => {
        handleSearch();
    }, [contacts, handleSearch]);

    const handleContactSelect = (user: UtilisateurChoice) => {
        const contact = contacts.find((c) => c.id?.toString() === user.id?.toString());
        if (contact) {
            onContactSelect(contact);
            modalActions.close();
        }
    };

    return (
        <Box
            sx={{
                width: '600px',
                padding: '30px',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
            }}
        >
            <Box>
                <Typography variant="l2dh6">Modifier le contact assigné</Typography>
                <Box sx={{ marginTop: '16px', marginBottom: '16px' }}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Sélectionnez un contact du prescripteur pour l&apos;assigner à ce dossier :
                    </Typography>
                </Box>
            </Box>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                    <CircularProgress />
                </Box>
            )}

            {!isLoading && contacts.length > 0 && (
                <UtilisateurSearchList
                    users={displayedContacts}
                    selectedUser={selectedContact}
                    onSelectUser={handleContactSelect}
                    listTitle="Contacts disponibles"
                    userInfo={(user) => user.email || ''}
                    onSearch={handleSearch}
                />
            )}

            {!isLoading && contacts.length === 0 && (
                <Box
                    sx={{
                        textAlign: 'center',
                        padding: '32px',
                        color: 'text.secondary',
                    }}
                >
                    <Typography variant="body2">
                        Aucun contact disponible pour ce prescripteur
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ContactSelectionModal;
